import React, { useCallback, useContext } from 'react';
import { Row, Col, Input, Select, Radio, Steps, Form } from 'antd';
import { useSelector } from 'react-redux';
import { Filter } from '@conviva/portal-graphql-schema';
import { useQuery } from '@apollo/react-hooks';
import { get, orderBy } from 'lodash';
import { Loading } from '@components/common';
import { featureSelector, bleEnabledSelector } from '@store/common/selectors';
import featureNames from '@utils/featureToggle/constants';
import { ALERT_FILTERS } from '@api/graphql/manualAlerts';
import { METRIC_TO_ALERT_METRIC_MAPPING } from '@utils/aiAlerts/alertMetrics';
import { FormContext } from '../../context';
import { getMetricInfo, MetricInfo } from '../../../constants';
import { FORM_HELP } from '../../constants';
import { StyledForm } from './styled';

const { Option } = Select;
// eslint-disable-next-line react-hooks/rules-of-hooks
export const filterMetrics = (allowedMetrics: string[], isCreating: boolean): MetricInfo => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const isBusinessMetricsEnabled = useSelector(bleEnabledSelector);
  const allowedAlertMetric = Object.entries(
    METRIC_TO_ALERT_METRIC_MAPPING(isBusinessMetricsEnabled),
  )
    .filter(([key]) => allowedMetrics.includes(key))
    .map(([_, value]) => value);
  const metricsInfo = getMetricInfo();
  let metrics = Object.entries(metricsInfo).filter(([abbr]) => allowedAlertMetric.includes(abbr));
  if (isCreating) {
    // this metrics is not supported for new alerts
    metrics = metrics.filter(([abbr]) => abbr !== 'ads_plays');
  }
  return metrics.reduce((acc, [abbr, info]) => {
    acc[abbr] = info;
    return acc;
  }, {});
};

// TODO: fix broken email validation
const GeneralForm = ({ form }) => {
  const { setFieldsValue } = form;
  const { alert } = useContext(FormContext);
  const { data } = useQuery(ALERT_FILTERS, { fetchPolicy: 'cache-and-network' });
  const filters: Filter[] = orderBy(get(data, 'self.customer.filters', []), (f) =>
    f.name.toLowerCase(),
  );
  const allowedMetrics = useSelector(featureSelector(featureNames.METRICS));
  const isCreating = get(alert, 'id', null) === null;
  const availableMetrics = filterMetrics(allowedMetrics, isCreating);
  const orderedMetrics = orderBy(Object.entries(availableMetrics), ([_, info]) => info.name);

  const metricValue = Form.useWatch('metric', form);
  const currentMetric = metricValue || get(alert, 'metric', orderedMetrics[0][0]);
  const currentMetricInfo = availableMetrics[currentMetric];

  const handleMetricChange = useCallback((v) => {
    setFieldsValue({ direction: availableMetrics[v].direction });
  }, []);

  if (!data) {
    return <Loading />;
  }

  return (
    <>
      <Row style={{ margin: '12px 0px' }}>
        <Steps current={0} items={[{ title: 'General' }, { title: 'Advanced' }]} />
      </Row>

      <StyledForm layout="vertical" form={form}>
        <Form.Item
          label="Alert Name"
          name="name"
          rules={[
            {
              required: true,
              min: 3,
              max: 256,
              message: 'Names must be between 3 and 256 characters.',
            },
          ]}
          initialValue={get(alert, 'name', '')}
        >
          <Input autoComplete="off" />
        </Form.Item>

        <Form.Item
          label="Filter"
          name="filterId"
          rules={[
            {
              required: true,
            },
          ]}
          initialValue={get(
            alert,
            'filter.id',
            (filters.find((f) => f.name === 'All Traffic') || {}).id,
          )}
        >
          <Select disabled={!isCreating}>
            {filters.map((filter) => (
              <Option key={filter.id} value={filter.id}>
                {filter.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Row gutter={8}>
          <Col span={12}>
            <Form.Item
              label="Metric"
              name="metric"
              rules={[
                {
                  required: true,
                },
              ]}
              initialValue={get(alert, 'metric', currentMetric)}
            >
              <Select onChange={handleMetricChange} disabled={!isCreating}>
                {orderedMetrics.map(([abbr, info]) => (
                  <Option key={abbr} value={abbr}>
                    {info.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item
              label="Condition"
              name="direction"
              rules={[
                {
                  required: true,
                },
              ]}
              initialValue={get(alert, 'direction', currentMetricInfo.direction)}
            >
              <Select>
                <Option value="<">{'<'}</Option>
                <Option value=">">{'>'}</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Threshold"
              name="threshold"
              tooltip={FORM_HELP.threshold}
              rules={[
                {
                  required: true,
                  validator: (_, value) =>
                    parseFloat(value) > 0
                      ? Promise.resolve()
                      : Promise.reject(new Error('Positive numbers only.')),
                },
              ]}
              initialValue={get(alert, 'threshold', '')}
            >
              <Input autoComplete="off" addonAfter={currentMetricInfo.units} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="Alert State"
          name="state"
          rules={[
            {
              required: true,
            },
          ]}
          initialValue={get(alert, 'state', true)}
        >
          <Radio.Group>
            <Radio value>Enabled</Radio>
            <Radio value={false}>Disabled</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          label="Notification Email"
          name="emails"
          tooltip={FORM_HELP.notification}
          rules={[
            {
              required: true,
              validator: (_, value) => {
                const values = value.split(',').map((v) => v.trim());
                return values.every((email) => /(.+)@(.+){2,}\.(.+){2,}/.test(email)) &&
                  values.length > 0
                  ? Promise.resolve()
                  : Promise.reject(
                      new Error(
                        'Valid email address formats only. Use a comma to separate multiple emails.',
                      ),
                    );
              },
            },
          ]}
          initialValue={get(alert, 'emails', '')}
        >
          <Input autoComplete="off" />
        </Form.Item>
      </StyledForm>
    </>
  );
};

export default GeneralForm;
